/**
 * Left-hand list of past workflow runs shown when the editor is in
 * Executions mode. Each row is clickable and sets the parent's
 * ``viewedRunId`` so the canvas overlays that run's per-node statuses
 * and the drawer surfaces recorded input/output.
 *
 * Layout chosen to mirror the n8n Executions panel (terse rows with
 * a status dot + timestamp + duration). Polling is left to react-query
 * staleTime — we don't want SSE flicker mid-debug.
 *
 * A non-terminal run (``running`` / ``waiting`` / ``resuming``) shows a
 * Stop button that cancels it via ``POST /workflow-executions/{id}/cancel``
 * — the operator must always be able to abort a run they kicked off (e.g.
 * a test-run that keeps going after they navigate away).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Clock, Loader2, OctagonX, XCircle } from "lucide-react";
import * as React from "react";

import { workflowExecutionsApi, type ExecutionSummary } from "@/lib/api-workflows";


const NON_TERMINAL = new Set(["running", "waiting", "resuming"]);


export interface ExecutionsListSidebarProps {
  workflowId: string;
  selectedRunId: string | null;
  onPickRun: (id: string | null) => void;
}


export function ExecutionsListSidebar({
  workflowId,
  selectedRunId,
  onPickRun,
}: ExecutionsListSidebarProps) {
  const runsQ = useQuery({
    queryKey: ["workflow", workflowId, "runs", "sidebar"],
    queryFn: () =>
      workflowExecutionsApi.list({ workflow_id: workflowId, page_size: 50 }),
    // 5s is a reasonable balance between freshness and not nuking the
    // user's scroll position every time a new run lands.
    refetchInterval: 5_000,
    staleTime: 4_000,
  });

  const items = runsQ.data?.items ?? [];

  // Auto-pick the most recent run the first time the list loads with
  // nothing selected. Means flipping to "Executions" mode immediately
  // shows the canvas overlay + logs footer, without forcing the user
  // to click into the sidebar before anything happens.
  React.useEffect(() => {
    if (selectedRunId === null && items.length > 0) {
      onPickRun(items[0].id);
    }
    // We intentionally don't depend on items.length alone — auto-pick
    // only fires when there's truly no selection (the user clearing it
    // back to null shouldn't trigger a re-pick).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length > 0, selectedRunId === null]);

  return (
    <aside className="flex h-full w-56 shrink-0 flex-col border-r border-border bg-surface">
      <div className="border-b border-border px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        Executions
      </div>
      <div className="flex-1 overflow-y-auto">
        {runsQ.isLoading && (
          <div className="px-3 py-4 text-[11px] text-muted-foreground">Loading…</div>
        )}
        {!runsQ.isLoading && items.length === 0 && (
          <div className="px-3 py-4 text-[11px] text-muted-foreground">
            No runs yet. Trigger one to see it here.
          </div>
        )}
        {items.map((r) => (
          <ExecutionRow
            key={r.id}
            workflowId={workflowId}
            run={r}
            selected={r.id === selectedRunId}
            onClick={() => onPickRun(r.id)}
          />
        ))}
      </div>
    </aside>
  );
}


function ExecutionRow({
  workflowId,
  run,
  selected,
  onClick,
}: {
  workflowId: string;
  run: ExecutionSummary;
  selected: boolean;
  onClick: () => void;
}) {
  const qc = useQueryClient();
  const ago = relativeAgo(run.started_at);
  const duration = formatDuration(run);
  const running = NON_TERMINAL.has(run.status);

  const cancelMut = useMutation({
    mutationFn: () => workflowExecutionsApi.cancel(run.id),
    onSettled: () => {
      // Refresh the list (this row flips to cancelled) + any open detail.
      qc.invalidateQueries({ queryKey: ["workflow", workflowId, "runs"] });
      qc.invalidateQueries({ queryKey: ["workflow-execution", run.id] });
    },
  });

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onClick();
      }}
      className={
        "group flex w-full cursor-pointer items-center gap-2 border-l-2 px-3 py-2 text-left text-[11px] transition hover:bg-muted/40 " +
        (selected ? "border-l-foreground bg-muted/50" : "border-l-transparent")
      }
    >
      <StatusIcon status={run.status} />
      <div className="min-w-0 flex-1">
        <div className="truncate font-mono text-[11px] text-foreground">
          {run.id.slice(0, 10).toUpperCase()}
        </div>
        <div className="truncate text-[10px] text-muted-foreground">
          {ago} · {duration}
        </div>
      </div>
      {running && (
        <button
          type="button"
          title="Interrompi esecuzione"
          aria-label="Interrompi esecuzione"
          disabled={cancelMut.isPending}
          onClick={(e) => {
            e.stopPropagation();
            cancelMut.mutate();
          }}
          className="shrink-0 rounded p-1 text-muted-foreground transition hover:bg-danger/10 hover:text-danger disabled:opacity-50"
        >
          {cancelMut.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />
          ) : (
            <OctagonX className="h-3.5 w-3.5" strokeWidth={1.5} />
          )}
        </button>
      )}
    </div>
  );
}


function StatusIcon({ status }: { status: ExecutionSummary["status"] }) {
  if (status === "succeeded") {
    return <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-success" strokeWidth={1.5} />;
  }
  if (status === "failed" || status === "cancelled") {
    return <XCircle className="h-3.5 w-3.5 shrink-0 text-danger" strokeWidth={1.5} />;
  }
  return <Clock className="h-3.5 w-3.5 shrink-0 text-amber-500" strokeWidth={1.5} />;
}


function relativeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}


function formatDuration(run: ExecutionSummary): string {
  if (!run.finished_at) return "running…";
  const ms = new Date(run.finished_at).getTime() - new Date(run.started_at).getTime();
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(2)}s`;
  return `${Math.floor(ms / 60_000)}m ${Math.floor((ms % 60_000) / 1000)}s`;
}
